import { ComponentType, PropsWithChildren, useReducer } from "react";
import * as React from "react";

export enum ProjectionProviderStatus {
  Ready = "ready",
  CanSubmit = "canSubmit",
  Creating = "creating",
  Finished = "finished",
  Error = "error",
  Initializing = "initializing"
}

export enum ProjectionProviderIdentifier {
  NPI = "npi",
  NPI2 = "npi2",
  TIN = "tin",
  NPITIN = "npitin",
  ContractEntity = "contractentity",
  CCN = "ccn",
  None = "",
}

export interface ProjectionProviderDetailsForm {
  allLocations: boolean;
  allProjects: boolean;
  allSpecialties: boolean;
  identifier: ProjectionProviderIdentifier;
  isPhoneInfoIconHovered: boolean;
  reasonDescription: string;
  reasonName: string;
  requireReRun: boolean;
  selectedExclusionVerifiedSources: string[];
  status: ProjectionProviderStatus;
  maxLength: number;
  disableRerunPreview: boolean;
  phone: string;
  hasChanged: boolean;
}

export interface ProjectionProviderDetailsProps {
  formState: ProjectionProviderDetailsForm;
  formDispatch: React.Dispatch<ProjectionProviderDetailsAction>;
}

export enum ProjectionProviderDetailsActionType {
  AllLocations = "allLocations",
  AllProjects = "allProjects",
  AllSpecialties = "allSpecialties",
  ContractEntity = "contractEntity",
  Identifier = "identifier",
  ReasonDescription = "reasonDescription",
  ReasonName = "reasonName",
  SelectedExclusionVerifiedSources = "selectedExclusionVerifiedSources",
  Finished = "finished",
  Initialize = "initialize",
  RunPreview = "runPreview",
  Reset = "reset",
  Phone = "phone",
  PhoneTooltip = "phoneTooltip",
}

export const projectionProviderDetailsInitialState: ProjectionProviderDetailsForm = {
  allLocations: false,
  allProjects: false,
  allSpecialties: false,
  disableRerunPreview: true,
  hasChanged: false,
  identifier: ProjectionProviderIdentifier.None,
  isPhoneInfoIconHovered: false,
  maxLength: 1000,
  phone: "",
  reasonDescription: "",
  reasonName: "",
  requireReRun: false,
  selectedExclusionVerifiedSources: [],
  status: ProjectionProviderStatus.Initializing,
}

export type ProjectionProviderDetailsAction =
  { type: ProjectionProviderDetailsActionType.AllLocations, payload: boolean }
  | { type: ProjectionProviderDetailsActionType.AllProjects, payload: boolean }
  | { type: ProjectionProviderDetailsActionType.AllSpecialties, payload: boolean }
  | { type: ProjectionProviderDetailsActionType.Identifier, payload: ProjectionProviderIdentifier }
  | { type: ProjectionProviderDetailsActionType.ReasonDescription, payload: string }
  | { type: ProjectionProviderDetailsActionType.ReasonName, payload: string }
  | { type: ProjectionProviderDetailsActionType.SelectedExclusionVerifiedSources, payload: string[] }
  | { type: ProjectionProviderDetailsActionType.Finished }
  | { type: ProjectionProviderDetailsActionType.RunPreview }
  | { type: ProjectionProviderDetailsActionType.Reset }
  | { type: ProjectionProviderDetailsActionType.Initialize, payload: ProjectionProviderDetailsForm }
  | { type: ProjectionProviderDetailsActionType.Phone, payload: string }
  | { type: ProjectionProviderDetailsActionType.PhoneTooltip, payload: boolean }
  | { type: ProjectionProviderDetailsActionType.ContractEntity, payload: ProjectionProviderIdentifier};

const projectionProviderDetailsReducer = (state: ProjectionProviderDetailsForm, action: ProjectionProviderDetailsAction): ProjectionProviderDetailsForm => {
  switch (action.type) {
    case ProjectionProviderDetailsActionType.Initialize:
      return {
        ...state,
        allLocations: action.payload.allLocations,
        allProjects: action.payload.allProjects,
        allSpecialties: action.payload.allSpecialties,
        disableRerunPreview: true,
        hasChanged: false,
        identifier: action.payload.identifier,
        isPhoneInfoIconHovered: false,
        maxLength: action.payload.maxLength,
        phone: action.payload.phone,
        reasonDescription: action.payload.reasonDescription,
        reasonName: action.payload.reasonName,
        requireReRun: action.payload.requireReRun,
        selectedExclusionVerifiedSources: action.payload.selectedExclusionVerifiedSources,
        status: action.payload.status
      };
    case ProjectionProviderDetailsActionType.Reset:
      return {
        ...projectionProviderDetailsInitialState,
        status: ProjectionProviderStatus.Initializing
      };
    case ProjectionProviderDetailsActionType.AllLocations:
      return {
        ...state,
        allLocations: action.payload,
        disableRerunPreview: false,
        hasChanged: true,
        phone: "",
        requireReRun: true,
        status: getStatus(state.reasonDescription, state.reasonName, state.maxLength, state.phone)
      };
    case ProjectionProviderDetailsActionType.AllProjects:
      return {
        ...state,
        allProjects: action.payload,
        hasChanged: true,
        requireReRun: true,
        status: getStatus(state.reasonDescription, state.reasonName, state.maxLength, state.phone)
      };
    case ProjectionProviderDetailsActionType.AllSpecialties:
      return {
        ...state,
        allSpecialties: action.payload,
        disableRerunPreview: false,
        hasChanged: true,
        requireReRun: true,
        status: getStatus(state.reasonDescription, state.reasonName, state.maxLength, state.phone)
      };
    case ProjectionProviderDetailsActionType.Identifier:
      return {
        ...state,
        disableRerunPreview: false,
        hasChanged: true,
        identifier: action.payload
      }
    case ProjectionProviderDetailsActionType.ReasonDescription:
      return {
        ...state,
        hasChanged: true,
        reasonDescription: action.payload,
        status: getStatus(action.payload, state.reasonName, state.maxLength, state.phone)
      }
    case ProjectionProviderDetailsActionType.ReasonName:
      return {
        ...state,
        hasChanged: true,
        reasonName: action.payload,
        status: getStatus(state.reasonDescription, action.payload, state.maxLength, state.phone)
      }
    case ProjectionProviderDetailsActionType.SelectedExclusionVerifiedSources:
      return {
        ...state,
        hasChanged: true,
        selectedExclusionVerifiedSources: action.payload,
        status: getStatus(state.reasonDescription, state.reasonName, state.maxLength, state.phone)
      }
    case ProjectionProviderDetailsActionType.Finished: {
      return {
        ...state,
        hasChanged: true,
        reasonDescription: state.reasonDescription.slice(0, state.maxLength),
        status: ProjectionProviderStatus.Finished
      }
    }
    case ProjectionProviderDetailsActionType.RunPreview: {
      return {
        ...state,
        disableRerunPreview: true,
        hasChanged: true
      }
    }
    case ProjectionProviderDetailsActionType.Phone: {
      return {
        ...state,
        allLocations: false,
        hasChanged: true,
        phone: action.payload,
        status: getStatus(state.reasonDescription, state.reasonName, state.maxLength, action.payload)
      }
    }
    case ProjectionProviderDetailsActionType.PhoneTooltip: {
      return {
        ...state,
        isPhoneInfoIconHovered: action.payload,
      }
    }
    case ProjectionProviderDetailsActionType.ContractEntity: {
      return {
        ...state,
        allLocations: true,
        allSpecialties: true,
        disableRerunPreview: false,
        identifier: action.payload,
      }
    }
    default:
      return state;
  }
}

const getStatus = (reasonDescription: string, reasonName: string, maxLength: number, phone: string): ProjectionProviderStatus => {
  return !!reasonName && (phone.length === 0 || phone.length === 10) && (reasonName !== "Other" || (reasonDescription && reasonDescription.length > 0 && reasonDescription.length < maxLength)) ? ProjectionProviderStatus.CanSubmit : ProjectionProviderStatus.Ready;
}

export default projectionProviderDetailsReducer;

export function withProjectionProviderDetailsReducer<T>(Component: ComponentType<PropsWithChildren<T & ProjectionProviderDetailsProps>>): ComponentType<PropsWithChildren<T>> {
  return function WrappedComponent(props) {
    const [projectionProvidersState, projectionProvidersReducer] = useReducer(projectionProviderDetailsReducer, projectionProviderDetailsInitialState);
    return <Component {...props} formState={projectionProvidersState} formDispatch={projectionProvidersReducer}/>;
  }
}
